import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PublicConsultationForm from './PublicConsultationForm';
import { API_BASE_URL } from '../services/api';
import './ConsultationFormPage.css';

interface TemplateResponse {
  id: string;
  template_name: string;
  template_description: string;
  form_fields: {
    fields: any[];
  };
  practice_area: string;
  country: string | null;
  visa_type: string | null;
  case_type: string | null;
}

const ConsultationFormPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const linkId = searchParams.get('link') || '';
  const practiceArea = searchParams.get('practiceArea') || '';
  const country = searchParams.get('country');
  const visaType = searchParams.get('visaType');
  const caseType = searchParams.get('caseType');
  const formIdParam = searchParams.get('formId') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<TemplateResponse | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [clientContext, setClientContext] = useState<{ name?: string | null; email?: string | null; phone?: string | null } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (formIdParam) {
          const response = await fetch(`${API_BASE_URL}/consultation-forms/${formIdParam}`);
          if (!response.ok) {
            throw new Error(response.status === 404 ? 'Consultation form not available.' : 'Failed to load consultation form.');
          }

          const data = await response.json();

          if (!data.template) {
            throw new Error('Template not found for this consultation.');
          }

          const resolvedPracticeArea = data.template.practice_area || practiceArea || 'immigration';
          const resolvedCountry = data.template.country ?? country ?? null;
          const resolvedVisaType = data.template.visa_type || visaType || null;
          const resolvedCaseType = data.template.case_type || caseType || null;

          setTemplate({
            id: data.template.id,
            template_name: data.template.template_name,
            template_description: data.template.template_description,
            form_fields: data.template.form_fields,
            practice_area: resolvedPracticeArea,
            country: resolvedCountry,
            visa_type: resolvedVisaType,
            case_type: resolvedCaseType
          });

          setClientContext({
            name: data.client_name || null,
            email: data.client_email || null,
            phone: data.client_phone || null
          });

          if (data.existingSubmission?.form_data) {
            setInitialValues(data.existingSubmission.form_data);
          } else {
            const defaults: Record<string, any> = {};
            if (data.client_name) {
              defaults.client_name = data.client_name;
              defaults.full_name = data.client_name;
            }
            if (data.client_email) {
              defaults.client_email = data.client_email;
              defaults.email = data.client_email;
            }
            if (data.client_phone) {
              defaults.client_phone = data.client_phone;
              defaults.phone = data.client_phone;
            }
            setInitialValues(Object.keys(defaults).length ? defaults : null);
          }

          setError(null);
          return;
        }

        if (!templateId || !linkId) {
          setError('Invalid consultation link.');
          return;
        }

        setClientContext(null);
        setInitialValues(null);

        const url = new URL(`${API_BASE_URL}/firm-link/${linkId}/templates`);
        url.searchParams.append('practiceArea', practiceArea);
        if (country) url.searchParams.append('country', country);
        if (visaType) url.searchParams.append('visaType', visaType);
        if (caseType) url.searchParams.append('caseType', caseType);

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(response.status === 404 ? 'Template not available.' : 'Failed to load template.');
        }

        const data = await response.json();
        const templates: TemplateResponse[] = data.templates || [];
        const match = templates.find(item => item.id === templateId);

        if (!match) {
          throw new Error('Template not found for this selection.');
        }

        setTemplate(match);
        setError(null);
      } catch (err) {
        console.error('❌ Failed to load consultation template:', err);
        setError(err instanceof Error ? err.message : 'Unable to load consultation form.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [templateId, linkId, practiceArea, country, visaType, caseType, formIdParam]);

  if (loading) {
    return (
      <div className="consultation-form-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading consultation form…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="consultation-form-page">
        <div className="error-container">
          <h2>Form Not Available</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="consultation-form-page">
        <div className="error-container">
          <h2>Form Not Found</h2>
          <p>We couldn’t find the requested consultation form.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consultation-form-page">
      <PublicConsultationForm
        linkId={linkId}
        template={template}
        practiceArea={template.practice_area || practiceArea}
        country={template.country || country}
        visaType={template.visa_type || visaType}
        caseType={template.case_type || caseType}
        formId={formIdParam || null}
        initialValues={initialValues}
        clientContext={clientContext}
      />
    </div>
  );
};

export default ConsultationFormPage;
